import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { PageLoading } from "../components/ui/Feedback";
import { AuthProvider, useAuth } from "../core/auth/AuthProvider";
import type { RequestPageMode } from "../features/requests/CampaignRequestPage";

const PhoneLoginPage = lazy(() =>
  import("../features/auth/PhoneLoginPage").then((module) => ({
    default: module.PhoneLoginPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("../features/auth/OnboardingPage").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const ApprovalPendingPage = lazy(() =>
  import("../features/auth/ApprovalPendingPage").then((module) => ({
    default: module.ApprovalPendingPage,
  })),
);
const DashboardPage = lazy(() =>
  import("../features/dashboard/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const CampaignsPage = lazy(() =>
  import("../features/campaigns/CampaignsPage").then((module) => ({
    default: module.CampaignsPage,
  })),
);
const CampaignRequestPage = lazy(() =>
  import("../features/requests/CampaignRequestPage").then((module) => ({
    default: module.CampaignRequestPage,
  })),
);
const ReportPage = lazy(() =>
  import("../features/report/ReportPage").then((module) => ({
    default: module.ReportPage,
  })),
);
const SettingsPage = lazy(() =>
  import("../features/settings/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const ExperienceQuestionsPage = lazy(() =>
  import("../features/experience-content/ExperienceContentPage").then(
    (module) => ({
      default: module.ExperienceQuestionsPage,
    }),
  ),
);
const ContentQuestionnaireListPage = lazy(() =>
  import("../features/experience-content/ExperienceContentPage").then(
    (module) => ({
      default: module.ContentQuestionnaireListPage,
    }),
  ),
);
const ContentExamplePage = lazy(() =>
  import("../features/experience-content/ExperienceContentPage").then(
    (module) => ({
      default: module.ContentExamplePage,
    }),
  ),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequestRoute({ mode }: { mode: RequestPageMode }) {
  return <CampaignRequestPage mode={mode} />;
}

function AuthGate() {
  const { stage } = useAuth();
  if (stage === "booting") return <PageLoading label="세션을 확인하는 중" />;
  if (stage === "anonymous")
    return (
      <Suspense fallback={<PageLoading />}>
        <PhoneLoginPage />
      </Suspense>
    );
  if (stage === "onboarding")
    return (
      <Suspense fallback={<PageLoading />}>
        <OnboardingPage />
      </Suspense>
    );
  if (stage === "pending")
    return (
      <Suspense fallback={<PageLoading />}>
        <ApprovalPendingPage />
      </Suspense>
    );

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route
            path="ai-validation"
            element={<Navigate to="/ai-validation/questions" replace />}
          />
          <Route
            path="ai-validation/questions"
            element={<ExperienceQuestionsPage />}
          />
          <Route
            path="ai-validation/questionnaires"
            element={<ContentQuestionnaireListPage />}
          />
          <Route
            path="ai-validation/content-examples"
            element={<ContentExamplePage />}
          />
          <Route
            path="ai-validation/content"
            element={<Navigate to="/ai-validation/questions" replace />}
          />
          <Route
            path="ai-validation/:legacy"
            element={<Navigate to="/ai-validation/questions" replace />}
          />
          <Route
            path="applicants"
            element={<RequestRoute mode="applicants" />}
          />
          <Route
            path="selection"
            element={<RequestRoute mode="selection" />}
          />
          <Route path="delivery" element={<RequestRoute mode="delivery" />} />
          <Route path="survey" element={<RequestRoute mode="survey" />} />
          <Route
            path="distribution"
            element={<RequestRoute mode="distribution" />}
          />
          <Route path="evidence" element={<RequestRoute mode="evidence" />} />
          <Route path="report" element={<ReportPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
